import { effect, inject, PendingTasks, Signal, signal, Type, untracked } from '@angular/core';

export type LazyComponentState =
  | { status: 'loading' }
  | { status: 'ready'; component: Type<unknown> }
  | { status: 'failed' };

export interface LazyComponentSource {
  load: () => Promise<Type<unknown>>;
  /** Names what failed in the console error, e.g. `widget type 'feed'` */
  label: string;
}

export interface LazyComponent {
  readonly state: Signal<LazyComponentState>;
  retry(): void;
}

/**
 * Loads the component `source` describes, again whenever `source` changes or on `retry()`.
 * A superseded or destroyed load neither updates the state nor holds the app unstable. Must be called in an
 * injection context.
 */
export function lazyComponent(source: () => LazyComponentSource | undefined): LazyComponent {
  const pendingTasks = inject(PendingTasks);
  const state = signal<LazyComponentState>({ status: 'loading' });
  const attempt = signal(0);

  effect((onCleanup) => {
    const current = source();
    attempt();
    state.set({ status: 'loading' });
    if (!current) {
      return;
    }
    let cancelled = false;
    const removeTask = pendingTasks.add();
    onCleanup(() => {
      cancelled = true;
      removeTask();
    });
    const settle = (next: LazyComponentState): void => {
      if (!cancelled) {
        state.set(next);
      }
      removeTask();
    };
    untracked(() => current.load()).then(
      (component) => settle({ status: 'ready', component }),
      (error: unknown) => {
        console.error(`Failed to load ${current.label}`, error);
        settle({ status: 'failed' });
      },
    );
  });

  return {
    state: state.asReadonly(),
    retry: () => attempt.update((count) => count + 1),
  };
}
